import { useContext } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PollingStatus } from '@/types/order';
import { Modal } from '@/components/Common/Modal';
import { Divider } from '@/components/Common/Divider';
import { TotalSummaryCard } from '@/components/TotalSummaryCard/TotalSummaryCard';
import { Payment } from '@/components/Payment/Payment';
import { Card3DSForm } from '@/components/Card3DSForm/Card3DSForm';
import { ProcessingLoader } from '@/components/ProcessingLoader/ProcessingLoader';
import { RedirectPayment } from '@/components/RedirectPayment/RedirectPayment';
import { SuccessScreen } from '@/components/SuccessScreen/SuccessScreen';
import { useCheckoutDimensionsReporter } from '@/hooks/useCheckoutDimensionsReporter';
import type { TemplateViewProps } from '@/templates/CheckoutTemplate';

/**
 * Mobile-only embedded checkout: a single white column topped by the compact
 * gray {@link TotalSummaryCard}, a hairline, then the standard payment stack.
 *
 *   ┌────────────────────────────────┐
 *   │  XButton                       │
 *   │  ┌──────────────────────────┐  │
 *   │  │ Total            $19.90 ⌃│  │  TotalSummaryCard
 *   │  │ ──────────────────────── │  │
 *   │  │ Subtotal         $19.90  │  │
 *   │  │ Tax (0%)             $0  │  │
 *   │  └──────────────────────────┘  │
 *   ├────────────────────────────────┤  divider
 *   │  express row (Apple / G Pay)   │
 *   │  "Or pay another way"          │  <Payment />
 *   │  method tiles → form → CTA     │
 *   │  Footer                        │
 *   └────────────────────────────────┘
 *
 * Below the divider this is DEFAULT verbatim — `<Payment />` reads the
 * EMBEDDED_VIEW feature flags, which mirror DEFAULT's for every payment-facing
 * concern (express row, method tiles, card-form header, internal Footer). The
 * entire delta from `DefaultView` is the top region: no publisher hero, no
 * offer header, no products list.
 *
 * `EmbeddedViewTemplate.isEligible` rejects true desktop at boot, so DEFAULT runs
 * there. The fallback below only catches a resize AFTER init (the template on
 * context is still EMBEDDED_VIEW at that point), matching `PreferredPaymentView`.
 */
export const EmbeddedView = ({ statusOrderPolling }: TemplateViewProps) => {
  const context = useContext(CheckoutContext);
  const {
    successScreen,
    redirectPayment: { redirectUrl, showRedirect },
  } = context;
  // Embedded checkout lives in a host-sized iframe, and this column is the one
  // that grows past it (sub-screens swap in place, APM forms differ in height),
  // so its height is reported to the SDK on every change.
  const containerRef = useCheckoutDimensionsReporter<HTMLDivElement>();

  return (
    <Modal>
      {/* `h-full overflow-y-auto` makes this column the single scroll viewport
          (same model as DEFAULT's mobile container): the top region and Payment
          are `flex-shrink-0` / `flex-1`, so the Footer that `<Payment />`
          renders sits at the bottom when content fits and scrolls with it when
          it doesn't. */}
      <div
        ref={containerRef}
        data-testid="embedded-view-checkout-container"
        // `checkout-embedded-view-font` scopes the publisher text face to this
        // subtree (the stylesheet is CDN-loaded at boot by `preloadEmbeddedViewFont`).
        className="checkout-embedded-view-font flex h-full w-full flex-col overflow-hidden bg-white"
      >
        <div data-testid="embedded-view-summary" className="flex w-full flex-shrink-0 flex-col pt-3">
          <div className="mt-2">
            <TotalSummaryCard />
          </div>
        </div>
        {/* EMBEDDED_VIEW never paints a publisher hero, so unlike DEFAULT there is
            no image edge to separate the summary from the payment column — the
            hairline is always rendered. */}
        <div className="my-4 w-full flex-shrink-0">
          <Divider />
        </div>
        <div className="relative flex flex-1 flex-col">
          <Payment />
        </div>
        <Card3DSForm />
        <ProcessingLoader />
        {(redirectUrl || showRedirect) && <RedirectPayment />}
        {successScreen?.isOpen && statusOrderPolling === PollingStatus.SUCCESS && <SuccessScreen />}
      </div>
    </Modal>
  );
};
